import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import AddTableCard from './AddTableCard';
import TableCard from './TableCard';
import { fetchWorkflowSteps } from '../../redux/Workflow/actions';
import {
  applyRemainingTatToLastLevel,
  clearLastLevelEscalationTime,
  formatMinutesToDuration,
} from '../../utils/duration';
import arrow1 from '../../public/icons/Arrow1.svg';
import arrow2 from '../../public/icons/Arrow2.svg';

import arrow3 from '../../public/icons/Arrow3.svg';
import arrow4 from '../../public/icons/Arrow4.svg';

const emptyTable = (id, name, values = {}) => ({
  id,
  name,
  role: Array.isArray(values.role)
    ? values.role
    : values.roleId !== undefined && values.roleId !== null
      ? [values.roleId]
      : [],
  canResolve: values.canResolve ?? '',
  canReassign: values.canReassign ?? '',
  finalLevel: values.finalLevel ?? '',
  escalateAfterMinutes: formatMinutesToDuration(values.escalateAfterMinutes) || '',
});

function toYesNo(value) {
  if (value === true || value === 'Yes' || value === 'true' || value === 1 || value === '1') return 'Yes';
  if (value === false || value === 'No' || value === 'false' || value === 0 || value === '0') return 'No';
  return value ?? '';
}

function mapWorkflowStep(value, index) {
  const roleIds = Array.isArray(value.role)
    ? value.role
    : Array.isArray(value.roleIds)
      ? value.roleIds
      : value.roleId !== undefined && value.roleId !== null
        ? [value.roleId]
        : [];
  return emptyTable(
    value.levelNo ?? index + 1,
    value.name ?? `Level ${value.levelNo ?? index + 1}`,
    {
      role: roleIds,
      canResolve: toYesNo(value.canResolve),
      canReassign: toYesNo(value.canReassign),
      finalLevel: toYesNo(value.isFinalLevel ?? value.finalLevel),
      escalateAfterMinutes: formatMinutesToDuration(value.escalateAfterMinutes) || '',
    },
  );
}

function mapWorkflowSteps(steps = []) {
  return (Array.isArray(steps) ? steps : []).map(mapWorkflowStep);
}

const defaultFormValues = [
  emptyTable(1, 'Level 1'),
];

function  TableConnector({ accent }) {
  return (
    <div className={`ca-escalation-connector ca-escalation-connector--desktop ca-escalation-connector--${accent}`} aria-hidden="true">
      <img src={arrow1} alt="" className="ca-escalation-connector__arrow ca-escalation-connector__arrow--first" />
      <span>Escalates to</span>
      <img src={arrow2} alt="" className="ca-escalation-connector__arrow ca-escalation-connector__arrow--second" />
    </div>
  );
}
function  VerticalConnector({ accent }) {
  return (
    <div className={`ca-escalation-connector ca-escalation-connector--mobile ca-escalation-connector--${accent}`} aria-hidden="true">
      <img src={arrow3} alt="" className="ca-escalation-connector__arrow ca-escalation-connector__arrow--first" />
      <span>Escalates to</span>
      <img src={arrow4} alt="" className="ca-escalation-connector__arrow ca-escalation-connector__arrow--second" />
    </div>
  );
}



export default function TableCardGrid({ id, selectedTableId, onSelect, setUpdatePayload , roleList = [], prefillSteps = [], tatTotalMinutes = 0}) {
  const hasWorkflowId = id !== undefined && id !== null;
  const [initialFormValues, setInitialFormValues] = useState(defaultFormValues);
  const [tables, setTables] = useState(defaultFormValues);
  const [isLoading, setIsLoading] = useState(hasWorkflowId);
  const [appliedFallback, setAppliedFallback] = useState(null);
  const [appliedTatTotalMinutes, setAppliedTatTotalMinutes] = useState(tatTotalMinutes);
  const gridRef = useRef(null);
  const tatTotalMinutesRef = useRef(tatTotalMinutes);
  const dispatch = useDispatch();
  const fallbackTables = useMemo(() => {
    const prefillValues = mapWorkflowSteps(prefillSteps);
    return prefillValues.length ? prefillValues : defaultFormValues;
  }, [prefillSteps]);

  if (!hasWorkflowId && appliedFallback !== fallbackTables) {
    const fallbackWithRemaining = applyRemainingTatToLastLevel(fallbackTables, tatTotalMinutes);
    setAppliedFallback(fallbackTables);
    setTables(fallbackWithRemaining);
    setInitialFormValues(fallbackWithRemaining);
  }

  if (!isLoading && appliedTatTotalMinutes !== tatTotalMinutes) {
    setAppliedTatTotalMinutes(tatTotalMinutes);
    const tablesWithRemaining = applyRemainingTatToLastLevel(tables, tatTotalMinutes);
    if (tablesWithRemaining !== tables) {
      setTables(tablesWithRemaining);
    }
  }

  useEffect(() => {
    tatTotalMinutesRef.current = tatTotalMinutes;
  }, [tatTotalMinutes]);

  useEffect(() => {
    if (!hasWorkflowId) {
      const fallbackWithRemaining = applyRemainingTatToLastLevel(fallbackTables, tatTotalMinutesRef.current);
      setUpdatePayload({ id, tables: fallbackWithRemaining });
      return undefined;
    }

    let cancelled = false;
    const loadWorkflowSteps = async () => {
      setIsLoading(true);

      try {
        const responseData = await dispatch(fetchWorkflowSteps(id)).unwrap();
        const fetchedValues = Array.isArray(responseData?.data)
          ? responseData.data
          : Array.isArray(responseData)
            ? responseData
            : [];
        const mappedValues = mapWorkflowSteps(fetchedValues);
        const nextValues = applyRemainingTatToLastLevel(
          mappedValues.length ? mappedValues : fallbackTables,
          tatTotalMinutesRef.current,
        );

        // console.log('[TableCardGrid] GET response:', responseData);
        // console.log('[TableCardGrid] initial form values:', nextValues);

        if (!cancelled) {
          setInitialFormValues(nextValues);
          setTables(nextValues);
          setUpdatePayload({ id, tables: nextValues });
        }
      } catch (error) {
        if (!cancelled) {
          const fallbackWithRemaining = applyRemainingTatToLastLevel(fallbackTables, tatTotalMinutesRef.current);
          setInitialFormValues(fallbackWithRemaining);
          setTables(fallbackWithRemaining);
          setUpdatePayload({ id, tables: fallbackWithRemaining });
          // console.log('[TableCardGrid] using fallback form values after GET error:', fallbackWithRemaining);
          console.error('Failed to load workflow steps:', error);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadWorkflowSteps();

    return () => {
      cancelled = true;
    };
  }, [dispatch, fallbackTables, hasWorkflowId, id, setUpdatePayload]);

  useEffect(() => {
    if (isLoading) return undefined;
    setUpdatePayload?.({ id, tables });
    return undefined;
  }, [id, isLoading, setUpdatePayload, tables]);

  const updateTables = (nextTables, { preserveLastLevel } = {}) => {
    const tablesToSave = preserveLastLevel
      ? nextTables
      : applyRemainingTatToLastLevel(nextTables, tatTotalMinutes);
    const payload = { id, tables: tablesToSave };
    setTables(tablesToSave);
    // onChange?.(nextTables);
    console.log('[TableCardGrid] updated form values:', tablesToSave);
    console.log('[TableCardGrid] PUT payload:', payload);
    setUpdatePayload?.(payload);
    // dispatch(updateWorkflowSteps(payload))
    //   .unwrap()
    //   .then((response) => console.log('[TableCardGrid] PUT response:', response))
    //   .catch((error) => console.error('[TableCardGrid] PUT failed:', error));
  };

  const isLastWorkflowLevel = (tableId) => {
    const lastLevel = tables[tables.length - 1];
    return lastLevel != null && lastLevel.id === tableId;
  };

  const updateTable = (tableId, changes) => {
    updateTables(
      tables.map((table) => table.id === tableId ? { ...table, ...changes } : table),
      { preserveLastLevel: isLastWorkflowLevel(tableId) },
    );
  };

  const updateTableFromSetter = (tableId, updater) => {
    console.log(initialFormValues, tables, tableId, updater);
    updateTables(
      tables.map((table) => {
        if (table.id !== tableId) return table;
        return typeof updater === 'function' ? updater(table) : updater;
      }),
      { preserveLastLevel: isLastWorkflowLevel(tableId) },
    );
  };

  const addTable = (name) => {
    const nextId = tables.reduce((highest, table) => Math.max(highest, Number(table.id) || 0), 0) + 1;
    const nextLevel = tables.length + 1;
    const tablesWithClearedPreviousLast = clearLastLevelEscalationTime(tables);
    updateTables([
      ...tablesWithClearedPreviousLast,
      emptyTable(nextId, name || `Level ${nextLevel}`),
    ]);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const scroller = gridRef.current;
        if (!scroller) return;
        scroller.scrollTo({ left: scroller.scrollWidth, behavior: 'smooth' });
      });
    });
  };

  const deleteTable = (tableId) => {
    updateTables(tables.filter((table) => table.id !== tableId));
  };

  if (isLoading && !tables.length) return null;

  return (
    <div className="ca-table-card-grid" aria-label="Escalation levels" ref={gridRef}>
      <div className="ca-table-card-grid__track">
        {tables.map((table, index) => (
          <React.Fragment key={table.id}>
            <TableCard
              name={table.name}
              roleList={roleList}
              level={index + 1}
              value={table}
              initialFormValues={table}
              setFormValues={(updater) => updateTableFromSetter(table.id, updater)}
              selected={table.id === selectedTableId}
              onSelect={() => onSelect?.(table)}
              onDelete={() => deleteTable(table.id)}
              onChange={(changes) => updateTable(table.id, changes)}
            />
            {index < tables.length - 0? (
              <>
                <TableConnector accent={index === 0 ? 'green' : index === 1 ? 'orange' : 'purple'} />
                <VerticalConnector accent={index === 0 ? 'green' : index === 1 ? 'orange' : 'purple'} />
              </>
            ) : null}
          </React.Fragment>
        ))}
        <AddTableCard level={tables.length + 1} onAdd={addTable} />
      </div>
    </div>
  );
}